import { Action, Reducer } from 'redux';
import { AppServices, TowState } from '../../AppServices';
import { AppContextRequestReponseType } from '../Models/Api/UserContextResponse';
import { UserContext } from '../Models/UserContext';
import { ToxnotServiceClient } from '../ToxnotServiceClient';
import { AppThunkAction, AppThunkActionAsync } from './../';

//
// This state controller knows how to fetch the user context from the service.
// Used when the app first kicks off.
//

// ===============================================================================

//
// Action definitions
//

const ReceiveAppServices = "RECEIVE_APP_SERVICES";
type ReceiveAppServicesType = typeof ReceiveAppServices;

interface ReceiveAppServicesAction {
    type: ReceiveAppServicesType;
    appServices: AppServices;
}

const TowTaskDelete = "TOW_TASK_DELETE";
type TowTaskDeleteType = typeof TowTaskDelete;

interface TowTaskDeleteAction {
    type: TowTaskDeleteType;
    towState: TowState | null;
}

// ===============================================================================

//
// Action creators
//

type KnownAction =
    ReceiveAppServicesAction |
    TowTaskDeleteAction;

export const actionCreators = {
    getAppServices: (): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const toxnotServiceClient = new ToxnotServiceClient();

        const result = await toxnotServiceClient.getAppServices();

        if (!result.success || (result.data === null)) {
            console.error("Failed to getAppServices: " + result.result);
            return;
        }

        if (result.data.userContextResponse.responseType === AppContextRequestReponseType.NoSeats) {
            window.location.href = "/Account/Team?seatsQuota=True";
            return;
        }

        const userContext = result.data.userContextResponse.userContextData;
        const isSignedIn = null !== userContext;
        const config = result.data.config;
        const activeTow = isSignedIn && result.data.activeTows.length > 0 ? result.data.activeTows[0] : null;

        //
        // Set up various 3rd party frameworks if the user is logged in
        //

        if (isSignedIn) {
            // don't turn Intercom chat on if we are in testing mode or a user has requested completely disabling chat
            if (!config.inTestingMode && !userContext.intercomIsDisabled) {
                const intercomChatEnabledSettingKey = `intercom-enabled-${userContext.userId}`;
                const intercomChatEnabledSettingValue = localStorage.getItem(intercomChatEnabledSettingKey);

                Intercom("boot", {
                    app_id: "kw8a37ox",
                    name: userContext.fullName,
                    email: userContext.emailAddress,
                    user_hash: userContext.intercomUserHash,
                    created_at: Math.round((userContext.signUpDate === null ? new Date() : new Date(userContext.signUpDate)).getTime() / 1000),
                    hide_default_launcher: (intercomChatEnabledSettingValue !== null) && (intercomChatEnabledSettingValue === "0"),
                });
            }

            var appId = "";
            if (config.isProductionServer) {
                appId = "1466764731";
            }
            else if (config.isBetaServer || config.isDevelopmentServer || config.isUatServer) {
                appId = "974980988";
            }

            // Only track app usage if signed in, and not a @toxnot.com address (this clause also covers test users). Also excluded is Bill Hoffman's personal account ignag@hotmail.com
            if (appId != "") {
                const heap = (window as any).heap;
                heap.load(appId);
                heap.identify(userContext.userId);
                const userProperties: any = {
                    'Subscription Type': userContext.subscription?.baseSubscriptionProduct.productName ?? 'Free',
                    'account_id': userContext.accountId,
                    'username': userContext.fullName,
                    'email': userContext.emailAddress,
                };

                if (userContext.fullName) {
                    userProperties['group_name'] = `${userContext.fullName}::${userContext.role ?? 'NoRoleAssigned'}`;
                }

                heap.addUserProperties(userProperties);
            }

            const pendoInstance = (window as any).pendo;

            if (pendoInstance) {
                const pendoVisitor: any = {
                    id: userContext.userId,
                    email: userContext.emailAddress,
                    full_name: userContext.fullName,
                    role: userContext.role
                };

                if (userContext.signUpDate) {
                    pendoVisitor.createdAt = userContext.signUpDate;
                }

                if (userContext.lastSignInDate) {
                    pendoVisitor.last_login = userContext.lastSignInDate;
                }

                pendoInstance.initialize({
                    visitor: pendoVisitor,
                    account: {
                        id: userContext.accountId
                    }
                });
            }
        }

        dispatch({
            type: ReceiveAppServices,
            appServices: new AppServices(
                userContext,
                activeTow,
                config,
                result.data.threeEAppStrip ?? { enabled: false })
        });
    },

    towResume: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        location.href = "/Publications/ResumeWorkflow";
    },

    towTaskUpdate: (towState: TowState): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const toxnotServiceClient = new ToxnotServiceClient();
        toxnotServiceClient.towUpdate({ taskId: towState.taskId, workflowType: towState.type, jsonState: towState.jsonState })
            .then(result => {
                if (!result.success) {
                    console.error("Failed to taskUpdate: " + result.result);
                    return;
                }

                // TODO: MJM SM2 actually resolve
                location.href = "/Publications/ResumeWorkflow";
            });
    },

    towTaskDelete: (towState: TowState): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const toxnotServiceClient = new ToxnotServiceClient();
        toxnotServiceClient.towDelete({ taskId: towState.taskId })
            .then(result => {
                if (!result.success) {
                    console.error("Failed to taskDelete: " + result.result);
                    return;
                }

                window.location.reload();

                // dispatch({ type: TowTaskDelete, towState: null });                
            });
    }
};

// ===============================================================================

//
// Reducer
//

export const reducer: Reducer<AppServices | null> = (state: AppServices | null | undefined, incomingAction: Action): AppServices | null => {
    if (state === undefined) {
        return null; // default state - no user context, it hasn't been fetched yet
    }

    switch (incomingAction.type) {
        case ReceiveAppServices:
            return (incomingAction as ReceiveAppServicesAction).appServices;            

        case TowTaskDelete:
            return AppServices.dupe(state!, null, state!.config);
    }

    return state;
}
